import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    PerspectiveAttribute,
    PerspectiveValueSource,
    PerspectiveValueType,
    SitePerspectiveType,
} from '@the-real-insight/tri-model';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { formatValue } from './perspectiveValues';
import { optionsOf } from './perspectiveAttributes';
import { FieldChange } from './FieldHistory';
import OverwriteCommentDialog, { OverwriteEntry } from './OverwriteCommentDialog';
import { confidenceOf } from './valueConfidence';
import { decideCommit, toInputValue } from './fieldCommit';
import { monthsOf, toIsoMonths } from './durationValue';
import PerspectiveFormRow from './PerspectiveFormRow';
import PhaseChecklists from './PhaseChecklists';
import PhaseParcels from './PhaseParcels';
import OrganizationChartEditor from './OrganizationChartEditor';
import MilestonePlan from './MilestonePlan';
import { PerspectiveScope } from './sitePerspectiveApi';
import { PhaseParcelResult, PhaseParcelState } from './phaseParcelsApi';
import NumericInput from '../components/NumericInput';
import { BoundSlotView, ListSlotEditor, ObjectSlotEditor, ReferencePicker, ResolvedSlot, SlotActions } from './SlotEditors';
import { NumericKind } from '../components/numericFormat';

/**
 * The editable form of one site perspective.
 *
 * Every attribute the perspective has appears, in the order the model lists them. An entered
 * attribute gets the input its type asks for; a derived one shows what the estimate worked out and
 * takes no typing, because a computed figure is not something to type over.
 *
 * **A field is saved when it is left.** There is no Save button and nothing to cancel. A drill-down
 * is read far more often than it is written and usually one figure at a time, and a form that holds
 * changes back needs the reader to know that it does: the earlier version collected edits, gathered
 * a reason at each blur, and wrote all of it only when Save was pressed — so a reason typed into the
 * dialog sat in memory, invisible in the history, until a button was found. Now the field, its
 * reason and its history entry all happen at the same moment.
 *
 * One field per write, and only the field that changed. The endpoint upserts partially, so the rest
 * of the perspective is left exactly as it is.
 */

interface Properties {
    type: SitePerspectiveType;
    attributes: PerspectiveAttribute[];
    values: Record<string, any> | undefined;
    /**
     * Writes what changed. Called with a single field, because a single field is what a blur is
     * about; `comments` carries its reason where one was given.
     */
    onSave: (
        type: SitePerspectiveType,
        values: Record<string, any>,
        comments?: Record<string, string>,
    ) => Promise<void>;
    /** Absent while no property is selected — there is nothing to write against. */
    disabled?: boolean;
    /** Which phase is on screen. The checklist point reads its rows from it. */
    scope?: PerspectiveScope | null;
    /** Phase parcel state from the page — every view of the plot shares one read. */
    parcelState?: PhaseParcelState;
    /** The building's outline, drawn under the parcels of the plot. */
    footprint?: any;
    /** Takes the whole plot back after a parcel decision — outlines, area and the figures it moved. */
    onParcelsChanged?: (result: PhaseParcelResult) => void;
    /** Listeneinträge anlegen und entfernen, Werte gebundener Instanzen schreiben. */
    slotActions?: SlotActions;
}

/** Which numeric field a value type asks for; text and date are not numbers. */
/**
 * Der Wert, unter dem „nicht beantwortet" im Auswahlfeld steht.
 *
 * Radix erlaubt keinen `SelectItem` mit leerem Wert — der leere String ist dort reserviert, um die
 * Auswahl zurückzusetzen. Also ein eigener Code, der beim Schreiben wieder zu `null` wird und
 * niemals in der Ablage landet.
 */
const UNSET = '__unset__';

/** The points shown as a block of their own under the fields. */
const BLOCK_TYPES = [
    PerspectiveValueType.checklists,
    PerspectiveValueType.parcels,
    PerspectiveValueType.organizationChart,
    PerspectiveValueType.milestones,
];

const NUMERIC_KIND: Partial<Record<PerspectiveValueType, NumericKind>> = {
    [PerspectiveValueType.money]: 'money',
    [PerspectiveValueType.decimal]: 'decimal',
    [PerspectiveValueType.percent]: 'percent',
    [PerspectiveValueType.integer]: 'integer',
};

export default function PerspectiveForm({
    type,
    attributes,
    values,
    onSave,
    disabled,
    scope,
    parcelState,
    footprint,
    onParcelsChanged,
    slotActions,
}: Properties) {
    const { t, i18n } = useTranslation();
    /**
     * What is typed but not yet left.
     *
     * Only the keystrokes: the moment a field is committed its entry goes, and the input falls back
     * to what came back from the server. Keeping it would leave the form showing the request rather
     * than the answer, and the two differ whenever the server coerces.
     */
    const [edits, setEdits] = useState<Record<string, any>>({});
    /** The field being written, so its row can say so. One at a time — a blur writes one field. */
    const [writing, setWriting] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    /** The field whose replacement is waiting on a reason, with the value it is waiting to write. */
    const [pending, setPending] = useState<{ attribute: PerspectiveAttribute; next: any; entry: OverwriteEntry } | null>(
        null,
    );

    const lang = i18n.language.startsWith('en') ? 'en' : 'de';
    const labelOf = (attribute: PerspectiveAttribute) =>
        attribute.label[lang as 'en' | 'de'] ?? attribute.label.de;

    // A different perspective discards what was typed against the old one — keeping it would show
    // one perspective's input in another's field.
    useEffect(() => {
        setEdits({});
        setPending(null);
        setError(null);
    }, [type]);

    /** A keystroke. It reaches the server only when the field is left. */
    const change = (name: string, value: any) => setEdits((current) => ({ ...current, [name]: value }));

    /**
     * Write one field, with the reason if one was given.
     *
     * The edit is dropped on success only. A write that failed leaves the typing where the reader
     * can see it and try again — silently reverting the field to the stored value would look like
     * the change had been rejected on its merits.
     */
    const writeField = async (attribute: PerspectiveAttribute, next: any, comment?: string) => {
        setWriting(attribute.name);
        setError(null);
        try {
            await onSave(type, { [attribute.name]: next }, comment ? { [attribute.name]: comment } : undefined);
            setEdits((current) => {
                const remaining = { ...current };
                delete remaining[attribute.name];
                return remaining;
            });
        } catch (saveError: any) {
            console.error('Could not save the perspective', saveError);
            setError(saveError?.message ?? t('global.generalError'));
        } finally {
            setWriting(null);
        }
    };

    /**
     * A field was left. Decide what that means and do it.
     *
     * The value is passed in rather than read from `edits`, because the blur is often the same event
     * that set it and state is not current yet.
     */
    const commit = (attribute: PerspectiveAttribute, next: any) => {
        const previous = values?.[attribute.name];
        const decision = decideCommit(attribute, previous, next);

        if (decision === 'none') {
            // Drop the keystrokes: they say the same as what is stored, and holding them would keep
            // the field out of step with the value it is showing.
            setEdits((current) => {
                const remaining = { ...current };
                delete remaining[attribute.name];
                return remaining;
            });
            return;
        }

        if (decision === 'write') {
            void writeField(attribute, next);
            return;
        }

        setPending({
            attribute,
            next,
            entry: {
                name: attribute.name,
                label: labelOf(attribute),
                previous: formatValue(attribute, previous),
                next: formatValue(attribute, next),
                confidence: confidenceOf(attribute, previous, values?.changes as FieldChange[]),
            },
        });
    };

    /**
     * Ein Objekt oder eine Referenz wurde geändert. Dieselbe Regel wie bei einem Wert — zum ersten Mal
     * gesetzt wird geschrieben, ersetzt wird mit Begründung —, nur verglichen über den ganzen Inhalt.
     */
    const commitStructured = (attribute: PerspectiveAttribute, next: any) => {
        const previous = values?.[attribute.name];
        const empty = (value: any) => value === null || value === undefined || value === '' || (typeof value === 'object' && Object.keys(value).length === 0);
        if (JSON.stringify(previous ?? null) === JSON.stringify(next ?? null)) return;
        if (empty(previous)) {
            void writeField(attribute, next);
            return;
        }
        setPending({
            attribute,
            next,
            entry: {
                name: attribute.name,
                label: labelOf(attribute),
                previous: formatValue(attribute, previous),
                next: formatValue(attribute, next),
                confidence: confidenceOf(attribute, previous, values?.changes as FieldChange[]),
            },
        });
    };

    /** Was der Server zu den gebundenen Slots aufgelöst hat. */
    const resolved: Record<string, ResolvedSlot> = (values?.resolved as Record<string, ResolvedSlot>) ?? {};

    /**
     * Ein Listeneintrag. Über die eigenen Wege des Servers, wo es sie gibt — sonst als ganze Liste,
     * was dasselbe Ergebnis hat, nur ohne Schutz vor zwei gleichzeitigen Einträgen.
     */
    const addItem = async (attribute: PerspectiveAttribute, item: Record<string, any>) => {
        setError(null);
        try {
            if (slotActions) await slotActions.changeListItem(type, attribute.name, { add: item });
            else await onSave(type, { [attribute.name]: [...((values?.[attribute.name] as any[]) ?? []), item] });
        } catch (failure: any) {
            setError(failure?.message ?? t('global.generalError'));
            throw failure;
        }
    };
    const removeItem = async (attribute: PerspectiveAttribute, key: string) => {
        setError(null);
        try {
            if (slotActions) await slotActions.changeListItem(type, attribute.name, { remove: key });
            else await onSave(type, { [attribute.name]: ((values?.[attribute.name] as any[]) ?? []).filter((entry) => entry?._key !== key) });
        } catch (failure: any) {
            setError(failure?.message ?? t('global.generalError'));
            throw failure;
        }
    };

    /** One answer to the dialog — a reason, or the decision not to give one. Either way it writes. */
    const answerOverwrite = (comment: string) => {
        if (!pending) return;

        const { attribute, next } = pending;
        setPending(null);
        void writeField(attribute, next, comment || undefined);
    };

    /**
     * The recorded changes of one field. They live on the perspective document as one list for all
     * fields, because that is how `Entity.changes` is written; the form only ever wants one field's.
     */
    const historyOf = (name: string): FieldChange[] =>
        ((values?.changes as FieldChange[] | undefined) ?? []).filter((entry) => entry.field === name);

    // Some points are tables or editors rather than values beside a label — the open tasks, the
    // parcels of the plot, the organization chart and the milestone plan. They belong under the
    // fields, not between two of them.
    const inputs = attributes.filter((attribute) => !BLOCK_TYPES.includes(attribute.valueType));
    const tablePoints = attributes.filter((attribute) => BLOCK_TYPES.includes(attribute.valueType));

    /**
     * A structure is written whole and straight away — without the reason dialog. The dialog asks
     * why one figure replaces another; an edit in a graph or a list is not one figure, and asking at
     * every added company would make the editor unusable.
     */
    const writeStructure = (name: string) => (next: any) => onSave(type, { [name]: next });

    return (
        <div>
            {inputs.map((attribute) => {
                const derived = attribute.source === PerspectiveValueSource.derived;
                const stored = toInputValue(attribute, values?.[attribute.name]);
                const shown = edits[attribute.name] ?? stored;

                /*
                 * Was ein Auswahlfeld zeigt, solange die Eingabe noch nicht geschrieben ist.
                 *
                 * Nicht `shown`: Das fällt über `??` auf den abgelegten Wert zurück, und ein soeben
                 * gewähltes „Nicht beantwortet" ist `null` — es spränge also auf den alten Wert
                 * zurück. Deshalb ausdrücklich aus der Eingabe, wenn es eine gibt, und erst sonst
                 * aus der Ablage.
                 */
                const pending = edits[attribute.name];
                const selectValue = pending !== undefined
                    ? (pending === null || pending === '' ? UNSET : String(pending))
                    : (stored === '' ? UNSET : stored);
                const numericKind = NUMERIC_KIND[attribute.valueType];
                const label = labelOf(attribute);
                // The field being written is the one that is out of the reader's hands; the rest
                // stay usable, because a write of one figure is no reason to freeze the form.
                const busy = writing === attribute.name;
                const bound = resolved[attribute.name];
                const slot = attribute as any;
                const structured =
                    attribute.valueType === PerspectiveValueType.object ||
                    attribute.valueType === PerspectiveValueType.list ||
                    bound?.cardinality === 'many';

                return (
                    <PerspectiveFormRow
                        key={attribute.name}
                        wide={structured}
                        label={label}
                        unit={
                            attribute.valueType === PerspectiveValueType.duration
                                ? t('portfolioDashboard.perspectiveForm.months')
                                : attribute.unit
                        }
                        changes={historyOf(attribute.name)}
                        confidence={confidenceOf(attribute, values?.[attribute.name], values?.changes as FieldChange[])}
                        format={(value) => formatValue(attribute, value)}
                        // Putting an old value back is a change like any other and takes the same
                        // road: it will be asked for a reason, and it is written straight away.
                        onRestore={(value) => {
                            change(attribute.name, value);
                            commit(attribute, value);
                        }}
                        disabled={disabled || busy}
                        saving={busy}
                    >
                        {bound?.cardinality === 'many' ? (
                            // Eine Zeile je Instanz — der Slot hängt an mehreren Baulasten, Anlagen, Verträgen.
                            <BoundSlotView
                                slot={slot}
                                resolved={bound}
                                disabled={disabled}
                                onSaveInstance={
                                    slotActions ? (ref, value) => slotActions.saveInstanceValue(type, attribute.name, ref, value) : undefined
                                }
                            />
                        ) : derived ? (
                            // Read-only, because a computed figure is not something to type over —
                            // but the figure itself, not a dash: what the estimate worked out is
                            // the whole point of the row.
                            <span className="block text-sm text-muted-foreground sm:text-right">
                                {formatValue(attribute, values?.[attribute.name])}
                            </span>
                        ) : attribute.valueType === PerspectiveValueType.object ? (
                            <ObjectSlotEditor
                                fields={slot.fields ?? []}
                                value={values?.[attribute.name]}
                                disabled={disabled || busy}
                                onCommit={(next) => commitStructured(attribute, next)}
                            />
                        ) : attribute.valueType === PerspectiveValueType.list ? (
                            <ListSlotEditor
                                item={slot.item}
                                value={values?.[attribute.name]}
                                disabled={disabled || busy}
                                onAdd={(item) => addItem(attribute, item)}
                                onRemove={(key) => removeItem(attribute, key)}
                                onEdit={(items) => writeField(attribute, items)}
                            />
                        ) : attribute.valueType === PerspectiveValueType.reference ? (
                            <ReferencePicker
                                target={slot.target ?? 'Entity'}
                                value={values?.[attribute.name]}
                                disabled={disabled || busy}
                                onChange={(next) => commitStructured(attribute, next)}
                            />
                        ) : numericKind ? (
                            // Numbers go through the shared field: grouped and rounded on blur, and
                            // it hands back a number, so nothing downstream sees a German comma.
                            <NumericInput
                                kind={numericKind}
                                value={
                                    edits[attribute.name] !== undefined
                                        ? edits[attribute.name]
                                        : values?.[attribute.name] ?? null
                                }
                                disabled={disabled || busy}
                                ariaLabel={label}
                                // `NumericInput` reports on blur, so this is the moment the field
                                // was left — no separate blur handler is needed or wanted.
                                onChange={(next) => {
                                    change(attribute.name, next);
                                    commit(attribute, next);
                                }}
                            />
                        ) : attribute.valueType === PerspectiveValueType.duration ? (
                            // A span is typed as months and stored as ISO 8601 ("P18M") — nobody
                            // should have to know that form to fill in a planning procedure.
                            <NumericInput
                                kind="integer"
                                value={
                                    edits[attribute.name] !== undefined
                                        ? monthsOf(edits[attribute.name])
                                        : monthsOf(values?.[attribute.name])
                                }
                                disabled={disabled || busy}
                                ariaLabel={label}
                                onChange={(months) => {
                                    const next = toIsoMonths(months);
                                    change(attribute.name, next);
                                    commit(attribute, next);
                                }}
                            />
                        ) : attribute.valueType === PerspectiveValueType.boolean ? (
                            /*
                             * Drei Zustände, kein Häkchen. Ein Kästchen kennt nur an und aus und
                             * zeigt „nicht beantwortet" als Nein — bei diesen Feldern ist das ein
                             * Unterschied, an dem etwas hängt: „Liegt im Wasserschutzgebiet: nein"
                             * ist eine Auskunft, „noch niemand nachgesehen" ist keine.
                             */
                            <Select
                                value={selectValue}
                                disabled={disabled || busy}
                                onValueChange={(value) => {
                                    const next = value === UNSET ? null : value === 'true';
                                    change(attribute.name, next);
                                    commit(attribute, next);
                                }}
                            >
                                <SelectTrigger aria-label={label}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={UNSET}>{t('portfolioDashboard.perspectiveForm.unset')}</SelectItem>
                                    <SelectItem value="true">{t('portfolioDashboard.perspectiveForm.yes')}</SelectItem>
                                    <SelectItem value="false">{t('portfolioDashboard.perspectiveForm.no')}</SelectItem>
                                </SelectContent>
                            </Select>
                        ) : attribute.valueType === PerspectiveValueType.selection && optionsOf(attribute).length ? (
                            <Select
                                value={selectValue}
                                disabled={disabled || busy}
                                onValueChange={(value) => {
                                    const next = value === UNSET ? null : value;
                                    change(attribute.name, next);
                                    commit(attribute, next);
                                }}
                            >
                                <SelectTrigger aria-label={label}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={UNSET}>{t('portfolioDashboard.perspectiveForm.unset')}</SelectItem>
                                    {optionsOf(attribute).map((option) => (
                                        <SelectItem key={option.name} value={option.name}>
                                            {option.label[lang] ?? option.label.de}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : attribute.valueType === PerspectiveValueType.text ? (
                            <Textarea
                                value={shown}
                                aria-label={label}
                                disabled={disabled || busy}
                                rows={2}
                                className="min-h-0 resize-none"
                                onChange={(event) => change(attribute.name, event.target.value)}
                                onBlur={(event) => commit(attribute, event.target.value)}
                            />
                        ) : (
                            <Input
                                value={shown}
                                aria-label={label}
                                disabled={disabled || busy}
                                type={attribute.valueType === PerspectiveValueType.date ? 'date' : 'text'}
                                onChange={(event) => change(attribute.name, event.target.value)}
                                onBlur={(event) => commit(attribute, event.target.value)}
                            />
                        )}
                        {bound?.cardinality === 'one' ? <BoundSlotView slot={slot} resolved={bound} /> : null}
                    </PerspectiveFormRow>
                );
            })}

            <OverwriteCommentDialog
                entry={pending?.entry ?? null}
                // Dismissed without a reason is an answer too: the change stands either way, which
                // is what the button says. Refusing to write it would make the dialog a gate, and
                // nobody agreed to defend every figure they enter.
                onDismiss={() => answerOverwrite('')}
                onConfirm={(comment) => answerOverwrite(comment)}
            />

            {disabled ? (
                <div className="mt-4 text-sm text-muted-foreground">
                    {t('portfolioDashboard.perspectiveForm.needsObject')}
                </div>
            ) : (
                <></>
            )}

            {error ? (
                <Alert variant="destructive" className="mt-3">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            ) : (
                <></>
            )}

            {tablePoints.map((attribute) => (
                <div key={attribute.name} className="mt-6 border-t pt-4">
                    <div className="mb-2 text-sm font-semibold">
                        {attribute.label[lang as 'en' | 'de'] ?? attribute.label.de}
                    </div>
                    {attribute.valueType === PerspectiveValueType.organizationChart ? (
                        <OrganizationChartEditor
                            value={values?.[attribute.name]}
                            onChange={writeStructure(attribute.name)}
                            disabled={disabled}
                        />
                    ) : attribute.valueType === PerspectiveValueType.milestones ? (
                        <MilestonePlan
                            value={values?.[attribute.name]}
                            onChange={writeStructure(attribute.name)}
                            disabled={disabled}
                        />
                    ) : attribute.valueType === PerspectiveValueType.parcels ? (
                        <PhaseParcels
                            parcelState={parcelState}
                            scope={scope ?? null}
                            footprint={footprint}
                            onParcelsChanged={onParcelsChanged}
                            canEdit={!disabled}
                        />
                    ) : (
                        <PhaseChecklists scope={scope ?? null} />
                    )}
                </div>
            ))}
        </div>
    );
}
